// Administration → Connecteurs: probe status (last probe, probing, last error), ACTUALISER
// (POST /api/connectors/refresh, can take a minute: progress with elapsed time), AJOUTER UN
// CONNECTEUR (while the office's connector library is on: Canva or any MCP address,
// myconnectors/add.js, then its connect window), one card per MCP server (engine, status, counts,
// floors that allow it) with an expandable tool list whose read/write classification can be
// overridden (PATCH /api/connectors/tools) or reset to automatic.
// A built-in server (the « Tables du bureau », `builtin: true`) keeps a fixed classification: its
// tools show their class without any control (the server refuses overrides for them).
// A connector of the library (`library: true`: added in the office) says who added it and when,
// its address host, its own state (à connecter, connecté, à reconnecter, en erreur, with the
// last error), that it is on every floor, and carries its actions (myconnectors/actions.js:
// CONNECTER / RECONNECTER / REPRENDRE, Vérifier, Renommer, Retirer — an administrator may manage
// any of them; a plugin the office serves itself, Shopify, Mailgun or Google Drive: SE CONNECTER /
// MODIFIER LA CLÉ / RECONNECTER / Réglages, its settings window). While the library is on,
// « Application Shopify » (admin/shopifyapp.js) sets the owner's app behind « Se connecter avec
// Shopify ». Whatever the library, « Application Google » (admin/drive.js) sets the office's Google
// app behind every Google Drive connector, lists them with the one storing the office's files and
// shows the server's disk (hidden on a server without it).
import { h, button, input, badge, segmented, statusDot, emptyState, spinner, icon, infoTip } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime, connectorLabel } from '../../format.js';
import { iconForConnector } from '../connectoricons.js';
import {
  sortServers, toolCounts, floorsAllowing, filterTools, withTool, connectorTone, errorText, serverEngineLabel,
} from './model.js';
import { sectionHead, loadingState, errorState, focusKeeper, setBusy } from './common.js';
import { createLibraryActions } from '../myconnectors/actions.js';
import { openAddConnector } from '../myconnectors/add.js';
import { childLayers } from '../automations/widgets.js';
import { stateView, addedByText, libraryAccess, isPluginConnector, isShopifyCli, settingsActionKey } from '../myconnectors/model.js';
import { createShopifyAppBlock } from './shopifyapp.js';
import { createDriveAppBlock } from './drive.js';
import { GDRIVE_ID } from '../myconnectors/drive-model.js';
import '../myconnectors/strings.js';
import './strings.js';

/** Refresh requests may take a while (Claude Code starts and lists its servers). */
export const REFRESH_TIMEOUT_MS = 180_000;
/** A filter box is shown above tool lists longer than this. */
const FILTER_MIN_TOOLS = 8;
/** While the server reports `probing`, poll this often. */
const POLL_MS = 3000;

/**
 * Normalises a GET/POST /api/connectors response (admin shape `{servers, probedAt, probing,
 * lastError}`; a bare array is accepted too).
 * @param {unknown} res
 * @returns {{servers: object[], probedAt: string|null, probing: boolean, lastError: string|null}}
 */
export function normalizeConnectors(res) {
  if (Array.isArray(res)) return { servers: res, probedAt: null, probing: false, lastError: null };
  const r = res && typeof res === 'object' ? res : {};
  return {
    servers: Array.isArray(r.servers) ? r.servers.filter((s) => s && typeof s === 'object' && s.key) : [],
    probedAt: r.probedAt || null,
    probing: r.probing === true,
    lastError: r.lastError ? String(r.lastError) : null,
  };
}

/**
 * A library server of GET /api/connectors as the library actions take it (an administrator may
 * manage any).
 * @param {object} s
 * @param {object|null} me
 */
export function libraryConnectorOf(s, me) {
  return {
    id: s.key,
    label: String(s.label || s.name || s.key),
    catalogId: s.catalogId || null,
    kind: s.kind === 'plugin' ? 'plugin' : 'remote',
    // A Shopify store connected through the Shopify CLI (its RECONNECTER is the CLI's login).
    ...(s.auth === 'cli' ? { plugin: { auth: 'cli' } } : {}),
    host: s.host || '',
    status: ['added', 'connected', 'needs-auth', 'error'].includes(s.status) ? s.status : 'added',
    lastError: s.lastError ? String(s.lastError) : '',
    addedBy: s.addedBy || null,
    addedByName: s.addedByName ? String(s.addedByName) : '',
    addedAt: s.addedAt || null,
    connectedAt: s.connectedAt || null,
    checkedAt: s.checkedAt || null,
    tools: [],
    toolCount: Array.isArray(s.tools) ? s.tools.length : 0,
    mine: !!me?.id && s.addedBy === me.id,
    canManage: true,
    allowedForMe: true,
  };
}

/**
 * Creates the connectors tab.
 * @param {object} ctx app context
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void}}
 */
export function createConnectorsView(ctx) {
  let data = null;
  let error = null;
  let refreshing = false;
  let refreshStarted = 0;
  let destroyed = false;
  let tick = null;
  let pollTimer = null;
  const expanded = new Set();
  const filters = new Map();
  const saving = new Set();
  const keeper = focusKeeper();
  const libraryOn = () => libraryAccess(ctx.store.get()).enabled;
  const windows = childLayers(null);

  const actions = createLibraryActions(ctx, {
    isOpen: () => !destroyed,
    list: () => (data?.servers || []).filter((s) => s.library === true).map((s) => ({ id: s.key, label: String(s.label || s.name || s.key) })),
    render: () => { if (data) render(); },
    onSaved: () => load({ quiet: true }),
    onRemoved: (c) => {
      if (data) data = { ...data, servers: data.servers.filter((x) => x.key !== c.id) };
      render();
      load({ quiet: true });
    },
    // A login succeeded, or a Google Drive's folder or storage changed: the cards (and the
    // « Application Google » block's list) are read again.
    onLoginDone: () => {
      load({ quiet: true });
      if (!destroyed) drive.reload();
    },
  });

  const refreshBtn = button(t('adm.conn.refresh'), { variant: 'primary', icon: 'refresh', onClick: () => refresh() });
  const addBtn = button(t('adm.conn.add'), { variant: 'secondary', icon: 'plus', class: 'adm-conn-add', onClick: () => openAdd() });
  addBtn.hidden = !libraryOn();
  // One sentence; the details (Claude Code, Codex, the library) in its « ? ».
  const head = sectionHead({
    title: t('adm.conn.title'), intro: [t('adm.conn.intro'), ' ', infoTip(t('adm.conn.introTip'))], actions: [addBtn, refreshBtn],
  });
  const probeEl = h('div', { class: 'adm-probe', role: 'status', 'aria-live': 'polite' });
  const secsEl = h('span', { class: 'adm-probe-secs mono-label', 'aria-hidden': 'true' });
  const body = h('div', { class: 'adm-body' }, loadingState());
  // The owner's Shopify app (« Se connecter avec Shopify »), while the library is on; what it says
  // (set, or the Shopify CLI there) tells a Shopify store to connect what « Se connecter » does.
  let shopifyRoute; // 'app' | 'cli' | null; undefined until the block has read the app
  const shopifyBlock = () => createShopifyAppBlock(ctx, {
    onChange: (app) => {
      const route = app?.configured ? 'app' : app?.cli ? 'cli' : null;
      if (route === shopifyRoute) return;
      shopifyRoute = route;
      if (data && !destroyed) render();
    },
  });
  let shopify = libraryOn() ? shopifyBlock() : null;
  const shopifyHost = h('div', { class: 'adm-shopify-host' }, shopify?.el);
  // The office's Google app (the Google Drive connectors, the office's media), never gated on the library.
  // What it reads of each Google Drive (its access, account and folder) is what its card says.
  let driveSeen = '';
  let driveConns = new Map(); // id → driveConnectorOf()
  const drive = createDriveAppBlock(ctx, {
    onChange: (status) => {
      const list = status?.connectors || [];
      const seen = JSON.stringify(list.map((c) => [c.id, c.connected, c.needsReconnect, c.mode, c.account, c.folder, c.storage]));
      if (seen === driveSeen) return;
      driveSeen = seen;
      driveConns = new Map(list.map((c) => [c.id, c]));
      if (data && !destroyed) render();
    },
  });
  const el = h('section', { class: 'adm-view adm-conns' }, head, probeEl, explainBox(), shopifyHost, drive.el, body);

  /** AJOUTER UN CONNECTEUR: the add panel in a modal (closed with the tab), then the new connector's connect window. */
  let addLayer = null;
  function openAdd() {
    if (destroyed) return null;
    // A second click while it is open (or still loading) keeps the one window.
    if (addLayer?.isOpen?.()) return addLayer;
    addLayer = windows.add(openAddConnector(ctx, {
      onAdded: (saved) => {
        if (destroyed) return;
        load({ quiet: true });
        // A remote one logs in now, a Shopify store through the Shopify CLI and a Google Drive too
        // (its connect window); another plugin is connected already (or on its way to Shopify).
        if (!isPluginConnector(saved) || isShopifyCli(saved) || saved.catalogId === GDRIVE_ID) actions.connect(saved);
      },
    }));
    return addLayer;
  }

  function explainBox() {
    return h('div', { class: 'adm-explain' },
      h('h4', { class: 'adm-explain-title mono-label' }, t('adm.conn.explain.title')),
      h('div', { class: 'adm-explain-grid' },
        h('p', { class: 'adm-explain-item is-read' }, h('span', { class: 'adm-kind-tag is-read' }, t('adm.conn.kind.read')), t('adm.conn.explain.read')),
        h('p', { class: 'adm-explain-item is-write' }, h('span', { class: 'adm-kind-tag is-write' }, t('adm.conn.kind.write')), t('adm.conn.explain.write'))),
      h('p', { class: 'adm-explain-note muted' }, t('adm.conn.explain.auto')));
  }

  async function load({ quiet = false } = {}) {
    try {
      const res = await ctx.api.get('/api/connectors');
      if (destroyed) return;
      data = normalizeConnectors(res);
      error = null;
    } catch (err) {
      if (destroyed) return;
      if (!quiet || !data) error = err;
    }
    render();
    schedulePoll();
  }

  /** While the server says a probe is running (started elsewhere), poll until it ends. */
  function schedulePoll() {
    clearTimeout(pollTimer);
    pollTimer = null;
    if (destroyed || refreshing || !data?.probing) return;
    pollTimer = setTimeout(() => load({ quiet: true }), POLL_MS);
  }

  async function refresh() {
    if (refreshing) return;
    refreshing = true;
    refreshStarted = Date.now();
    setBusy(refreshBtn, true);
    renderProbe();
    clearInterval(tick);
    // Only the (aria-hidden) seconds counter changes every second, so the live region is not
    // announced again and again.
    tick = setInterval(() => { secsEl.textContent = ` ${Math.round((Date.now() - refreshStarted) / 1000)} s`; }, 1000);
    try {
      const res = await ctx.api.post('/api/connectors/refresh', {}, { timeoutMs: REFRESH_TIMEOUT_MS });
      if (destroyed) return;
      data = normalizeConnectors(res);
      error = null;
      if (data.lastError) ctx.toast.warn(t('adm.conn.lastError', { error: data.lastError }));
      else ctx.toast.success(t('adm.conn.refreshed', { n: data.servers.length }));
    } catch (err) {
      if (destroyed) return;
      ctx.toast.error(err);
    } finally {
      refreshing = false;
      clearInterval(tick);
      tick = null;
      if (!destroyed) {
        setBusy(refreshBtn, false);
        render();
      }
    }
  }

  async function setKind(server, tool, kind) {
    if (saving.has(tool.full)) return;
    saving.add(tool.full);
    render();
    try {
      const res = await ctx.api.patch('/api/connectors/tools', { full: tool.full, kind });
      if (destroyed) return;
      const next = res?.tool && res.tool.full ? res.tool : { ...tool, kind: kind || tool.kind, overridden: kind !== null };
      data = { ...data, servers: withTool(data.servers, next) };
      ctx.toast.success(kind === null
        ? t('adm.conn.overrideReset', { name: tool.name })
        : t('adm.conn.overrideSaved', { name: tool.name, kind: t(`adm.conn.kind.${next.kind}`).toLowerCase() }), { timeout: 2500 });
    } catch (err) {
      if (!destroyed) ctx.toast.error(errorText(err));
    } finally {
      saving.delete(tool.full);
      if (!destroyed) render();
    }
  }

  // --- rendering --------------------------------------------------------------------------------
  function renderProbe() {
    if (refreshing || data?.probing) {
      secsEl.textContent = refreshing ? ` ${Math.max(0, Math.round((Date.now() - refreshStarted) / 1000))} s` : '';
      probeEl.replaceChildren(
        h('div', { class: 'adm-probe-row is-busy' }, spinner(t('adm.conn.probing')),
          h('span', { class: 'adm-probe-text' }, t('adm.conn.probing'), secsEl)),
        h('p', { class: 'adm-probe-hint muted' }, t('adm.conn.probingHint')));
      return;
    }
    const parts = [];
    if (data?.probedAt) {
      parts.push(h('div', { class: 'adm-probe-row' }, icon('clock', { size: 16 }),
        h('span', { class: 'adm-probe-text', title: formatDateTime(data.probedAt) }, t('adm.conn.probedAt', { when: relativeTime(data.probedAt) }))));
    } else if (data) {
      parts.push(h('div', { class: 'adm-probe-row' }, icon('info', { size: 16 }), h('span', { class: 'adm-probe-text' }, t('adm.conn.neverProbed'))));
    }
    if (data?.lastError) {
      parts.push(h('p', { class: 'adm-alert is-danger', role: 'alert' }, icon('alert', { size: 16 }), h('span', {}, t('adm.conn.lastError', { error: data.lastError }))));
    }
    if (ctx.store.get().office?.mock || ctx.store.get().engines?.mock?.forced) {
      parts.push(h('p', { class: 'adm-alert is-info' }, icon('info', { size: 16 }), h('span', {}, t('adm.conn.mock'))));
    }
    probeEl.replaceChildren(...parts);
  }

  function render() {
    const restore = keeper.begin();
    renderProbe();
    if (error && !data) {
      head.setCount('');
      refreshBtn.disabled = error.status === 404;
      body.replaceChildren(errorState(error, {
        onRetry: () => { body.replaceChildren(loadingState()); load(); }, unavailableText: t('adm.conn.unavailable'),
      }));
      return;
    }
    if (!data) return;
    if (!refreshing) refreshBtn.disabled = false;
    const servers = sortServers(data.servers);
    head.setCount(t('adm.conn.count', { n: servers.length }));
    if (!servers.length) {
      const on = libraryOn();
      body.replaceChildren(emptyState({
        icon: 'layers', title: t('adm.conn.empty.title'), text: t(on ? 'adm.conn.empty.text' : 'adm.conn.empty.textNoLibrary'),
        action: on ? keeper.reg('empty-add', button(t('adm.conn.add'), { variant: 'primary', size: 'sm', icon: 'plus', onClick: () => openAdd() })) : undefined,
      }));
      restore();
      return;
    }
    body.replaceChildren(h('div', { class: 'adm-conn-grid' }, servers.map(serverCard)));
    restore();
  }

  /**
   * The library part of a card: who added it, its state's text, its actions. A Google Drive's text
   * is always there: the folder it is limited to (a lock), or the folder still to choose.
   */
  function libraryParts(s) {
    const c = libraryConnectorOf(s, ctx.store.get().me);
    const gd = c.catalogId === GDRIVE_ID ? driveConns.get(c.id) : null;
    if (gd) c.plugin = { mode: gd.mode || 'limited', account: gd.account, folder: gd.folder, storage: gd.storage };
    const running = actions.running.has(c.id);
    const v = stateView(c, { jobActive: running, canManage: true, shopifyRoute });
    const now = Date.now() + (Number(ctx.store.get().serverOffset) || 0);
    const meta = [
      c.addedAt ? `${addedByText(c)} ${relativeTime(c.addedAt, now)}` : addedByText(c),
      c.host,
      c.checkedAt ? t('pc.checkedAt', { when: relativeTime(c.checkedAt, now) }) : '',
    ].filter(Boolean).join(' · ');
    const busy = actions.busy.has(c.id);
    const act = (action, opts) => keeper.reg(`${action}:${c.id}`, button(t(`pc.action.${action}`), {
      size: 'sm', disabled: busy, ariaLabel: t('pc.actionAria', { action: t(`pc.action.${action}`), name: c.label }), ...opts,
    }));
    const buttons = [];
    if (v.primary === 'settings') {
      buttons.push(act(v.settingsLabel, { variant: 'primary', icon: v.primaryIcon || 'key', onClick: () => actions.settings(c) }));
    } else if (v.primary) {
      const resume = v.primary === 'resume';
      buttons.push(act(v.primary, { variant: 'primary', icon: 'key', onClick: () => actions.connect(c, { resume }) }));
    }
    if (v.settings) {
      const label = t(`pc.action.settings.${settingsActionKey(c)}`);
      buttons.push(keeper.reg(`settings:${c.id}`, button(label, {
        size: 'sm', variant: 'ghost', icon: 'key', disabled: busy, ariaLabel: t('pc.actionAria', { action: label, name: c.label }),
        onClick: () => actions.settings(c),
      })));
    }
    if (v.reconnect) buttons.push(act('reconnect', { variant: 'ghost', icon: 'key', onClick: () => actions.reconnect(c) }));
    if (v.check) buttons.push(act('check', { variant: 'ghost', icon: 'refresh', onClick: () => actions.check(c) }));
    buttons.push(act('rename', { variant: 'ghost', icon: 'pencil', onClick: () => actions.rename(c) }));
    buttons.push(act('remove', { variant: 'ghost', icon: 'close', class: 'adm-del', onClick: () => actions.remove(c) }));
    // The state's own words when they say more than its label (a last error, what to do); a Google
    // Drive's once its folder is known (what the office may see, with its lock).
    const text = c.status !== 'connected' || (gd && c.status === 'connected')
      ? h('p', { class: ['adm-conn-libtext', `is-${v.needsFolder ? 'needs-folder' : c.status}`, { 'has-lead': v.lead }] },
        v.lead ? icon(v.lead, { size: 15, class: 'adm-conn-lead' }) : null, h('span', {}, v.text))
      : null;
    return {
      c,
      v,
      meta: h('p', { class: 'adm-conn-libmeta mono-label', title: c.addedAt ? formatDateTime(c.addedAt, now) : null }, meta),
      text,
      running: running ? h('p', { class: 'adm-conn-running' }, t('pc.jobRunning')) : null,
      actions: h('div', { class: 'adm-conn-actions' }, buttons),
      busy,
    };
  }

  function serverCard(s) {
    const library = s.library === true;
    const label = library ? String(s.label || s.name || s.key) : connectorLabel(s.name || s.key);
    const counts = toolCounts(s);
    const floors = library ? [] : floorsAllowing(ctx.store.get().office?.floors, s.key);
    const open = expanded.has(s.key);
    const tools = Array.isArray(s.tools) ? s.tools : [];
    const panelId = `adm-tools-${String(s.key).replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const lib = library ? libraryParts(s) : null;
    // A Google Drive connected without its folder is not done yet: amber, « dossier à choisir ».
    const needsFolder = lib?.v.needsFolder === true;
    const statusLabel = needsFolder ? t('adm.conn.needsFolder')
      : t(`connector.status.${['connected', 'needs-auth', 'failed', 'added', 'error'].includes(s.status) ? s.status : 'unknown'}`);
    const tone = needsFolder ? 'warn' : connectorTone(s.status);
    const toggle = tools.length ? keeper.reg(`toggle:${s.key}`, h('button', {
      type: 'button', class: 'adm-conn-toggle', 'aria-expanded': String(open), 'aria-controls': panelId,
      onClick: () => { if (expanded.has(s.key)) expanded.delete(s.key); else expanded.add(s.key); render(); },
    }, h('span', {}, open ? t('adm.conn.hideTools') : t('adm.conn.showTools', { n: tools.length })),
    icon(open ? 'chevronUp' : 'chevronDown', { size: 14 }))) : null;

    return h('article', {
      class: ['adm-conn', `is-${tone}`, { 'is-builtin': s.builtin === true, 'is-library': library, 'is-busy': lib?.busy }],
      dataset: { key: s.key },
    },
    h('header', { class: 'adm-conn-head' },
      iconForConnector(library ? { key: s.catalogId || s.key, name: label } : s, { size: 32 }),
      h('div', { class: 'adm-conn-titles' },
        h('h4', { class: 'adm-conn-name', title: library ? label : null }, label),
        h('p', { class: 'adm-conn-status' }, statusDot(tone), h('span', {}, statusLabel))),
      s.builtin === true ? badge(t('adm.conn.builtin'), 'dark') : null,
      library ? badge(t('adm.conn.library'), 'info', { title: t('adm.conn.libraryHint') }) : null,
      badge(serverEngineLabel(s), s.engine === 'codex' && s.builtin !== true ? 'info' : 'neutral')),
    s.builtin === true ? h('p', { class: 'adm-conn-builtin muted' }, t('adm.conn.builtinHint')) : null,
    lib?.meta,
    lib?.text,
    lib?.running,
    h('p', { class: 'adm-conn-counts' },
      s.engine === 'codex' && !tools.length
        ? h('span', { class: 'muted' }, t('adm.conn.codexTools'))
        : [h('span', { class: 'adm-conn-total' }, t('adm.conn.tools', { n: counts.total })), ' · ',
          t('adm.conn.split', { read: counts.read, write: counts.write }),
          counts.overridden ? h('span', { class: 'adm-conn-manual mono-label' }, ` · ${counts.overridden} ${t('adm.conn.manual')}`) : null]),
    h('p', { class: 'adm-conn-floors' }, library
      ? t('adm.conn.everyFloor')
      : floors.length
        ? t('adm.conn.allowedOn', { list: floors.join(', ') })
        : h('span', { class: 'muted' }, t('adm.conn.allowedNowhere'))),
    lib?.actions,
    toggle,
    tools.length ? toolPanel(s, panelId, open)
      : (s.engine !== 'codex' ? h('p', { class: 'adm-conn-notools muted' }, t(library && s.status !== 'connected' ? 'adm.conn.libraryNoTools' : 'adm.conn.noTools')) : null));
  }

  function toolPanel(s, panelId, open) {
    const tools = s.tools;
    const list = h('ul', { class: 'adm-tools', 'aria-label': t('adm.conn.showTools', { n: tools.length }) });
    const q = filters.get(s.key) || '';
    const fillList = () => {
      const shown = filterTools(tools, filters.get(s.key) || '');
      list.replaceChildren(...(shown.length ? shown.map((tool) => toolRow(s, tool)) : [h('li', { class: 'adm-tools-empty muted' }, t('adm.conn.noMatch'))]));
    };
    let filter = null;
    if (open && tools.length > FILTER_MIN_TOOLS) {
      filter = keeper.reg(`filter:${s.key}`, input({
        type: 'search', class: 'input-sm adm-tools-filter', value: q, placeholder: t('adm.conn.filter'), 'aria-label': t('adm.conn.filter'),
        onInput: (e) => { filters.set(s.key, e.target.value); fillList(); },
      }));
    }
    if (open) fillList();
    return h('div', { class: 'adm-tools-panel', id: panelId, hidden: !open }, filter, open ? list : null);
  }

  function toolRow(s, tool) {
    const kind = tool.kind === 'read' ? 'read' : 'write';
    if (s.builtin === true) {
      return h('li', { class: ['adm-tool', `is-${kind}`, 'is-fixed'] },
        h('span', { class: 'adm-tool-name', title: tool.full }, tool.name || tool.full),
        h('span', { class: 'adm-tool-ctrl', title: t('adm.conn.fixedHint') },
          h('span', { class: ['adm-kind-tag', `is-${kind}`] }, t(`adm.conn.kind.${kind}`))));
    }
    const busy = saving.has(tool.full);
    const seg = segmented([
      { value: 'read', label: t('adm.conn.kind.read') },
      { value: 'write', label: t('adm.conn.kind.write') },
    ], {
      value: tool.kind === 'read' ? 'read' : 'write', size: 'sm', label: t('adm.conn.kindAria', { name: tool.name }),
      onChange: (v) => { if (v !== tool.kind) setKind(s, tool, v); },
    });
    seg.classList.add('adm-kind');
    for (const b of seg.childNodes) {
      keeper.reg(`kind:${tool.full}:${b.dataset.value}`, b);
      if (busy) b.disabled = true;
    }
    const reset = tool.overridden ? keeper.reg(`reset:${tool.full}`, button(t('adm.conn.reset'), {
      variant: 'link', size: 'sm', ariaLabel: t('adm.conn.resetAria', { name: tool.name }), disabled: busy,
      onClick: () => setKind(s, tool, null),
    })) : null;
    return h('li', { class: ['adm-tool', `is-${tool.kind === 'read' ? 'read' : 'write'}`, { 'is-busy': busy }] },
      h('span', { class: 'adm-tool-name', title: tool.full }, tool.name || tool.full,
        tool.overridden ? h('span', { class: 'adm-tool-manual mono-label', title: t('adm.conn.manualHint') }, t('adm.conn.manual')) : null),
      h('span', { class: 'adm-tool-ctrl' }, seg, reset));
  }

  const offs = [
    ctx.events.on('connectors', () => { if (!refreshing) load({ quiet: true }); }),
    // A login left finishing ended (its live event): the card no longer says « en cours ».
    ctx.events.on('connector-library', (d) => {
      const id = d?.connector?.id;
      if (id && actions.running.delete(id) && data) render();
    }),
    ctx.store.on('office', () => { if (data) render(); }),
    ctx.store.on('features', () => {
      addBtn.hidden = !libraryOn();
      if (libraryOn() && !shopify) {
        shopify = shopifyBlock();
        shopifyHost.replaceChildren(shopify.el);
      } else if (!libraryOn() && shopify) {
        shopify.destroy();
        shopify = null;
        shopifyHost.replaceChildren();
      }
      if (data) render();
    }),
  ];
  load();
  return {
    el,
    reload: () => {
      drive.reload();
      return load({ quiet: true });
    },
    destroy() {
      destroyed = true;
      clearInterval(tick);
      clearTimeout(pollTimer);
      for (const off of offs) off();
      actions.closeAll();
      windows.closeAll('route');
      shopify?.destroy();
      drive.destroy();
    },
  };
}
